/* eslint-disable max-lines */
import { Calendar, Clock, ExternalLink, MessageSquare } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import type { IncidentResolution } from '@/lib/queries/rca/incidentResolutions';
import type { Incident, IncidentSource } from '@/lib/queries/rca/incidents';

import { IncidentStatusBadge } from '../../-components/IncidentStatus';
import { PriorityBadge } from '../../-components/SeverityBadge';
import {
  getPrimaryIncidentData,
  getSourceIcon,
  getSourceName,
} from './IncidentAlertHelpers';

const getSourcesUrls = (
  incident: Incident,
): { source: IncidentSource; url: string }[] => {
  return incident.sources
    .map(source => {
      switch (source) {
        case 'pagerduty':
          return {
            source,
            url: incident.pagerDutyIncident?.url,
          };
        case 'slack':
          return {
            source,
            url: incident.slackIncident?.url,
          };
      }
    })
    .filter(
      (sourceUrl): sourceUrl is { source: IncidentSource; url: string } => {
        return sourceUrl.url !== undefined;
      },
    );
};

interface IncidentAlertProps {
  incident: Incident;
  startIncidentResolution: () => void;
  incidentResolution: IncidentResolution | undefined;
}

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const IncidentHeader = ({
  incidentNumber,
  sourceStatus,
  priority,
  sources,
  startIncidentResolution,
  incidentResolution,
}: {
  incidentNumber?: number;
  sourceStatus: string;
  priority?: string;
  sources: { source: IncidentSource; url: string }[];
  startIncidentResolution: () => void;
  incidentResolution: IncidentResolution | undefined;
}) => (
  <div className="mb-4">
    <div className="flex items-center gap-2 mb-2">
      <div className="flex items-center gap-1.5">
        {sources.map(source => (
          <div
            key={source.source}
            className="flex-shrink-0 w-6 h-6 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center"
          >
            <img
              src={getSourceIcon(source.source)}
              alt={source.source}
              className="w-4 h-4"
            />
          </div>
        ))}
      </div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        <FormattedMessage
          id="rca.details.incidentTitle"
          values={{ number: incidentNumber }}
        />
      </h2>
    </div>
    <div className="flex items-center gap-2 mb-3">
      <div>
        <IncidentStatusBadge
          sourceStatus={
            sourceStatus as 'triggered' | 'acknowledged' | 'resolved'
          }
        />
      </div>
      {priority !== undefined && priority !== '' && (
        <PriorityBadge priority={priority} />
      )}
    </div>
    <div className="gap-2 flex flex-col">
      {incidentResolution === undefined && (
        <Button
          variant="main"
          size="sm"
          onClick={() => startIncidentResolution()}
        >
          Start Investigation
        </Button>
      )}
      {sources.map(({ source, url }) => (
        <a href={url} key={source} target="_blank" rel="noopener noreferrer">
          <Button variant="outline" size="sm" className="w-full">
            <ExternalLink className="w-4 h-4 mr-2" />
            <FormattedMessage
              id="rca.details.viewIn"
              values={{ platform: getSourceName(source) }}
            />
          </Button>
        </a>
      ))}
    </div>
  </div>
);

export const IncidentAlert = ({
  incident,
  startIncidentResolution,
  incidentResolution,
}: IncidentAlertProps): JSX.Element => {
  const primaryData = getPrimaryIncidentData(incident);

  if (!primaryData) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <p className="text-gray-600 dark:text-gray-400">
          <FormattedMessage id="rca.details.noIncidentData" />
        </p>
      </div>
    );
  }

  const {
    incidentNumber,
    title,
    summary,
    priority,
    startedAt,
    resolvedAt,
    sourceStatus,
  } = primaryData;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <IncidentHeader
        incidentNumber={incidentNumber}
        sourceStatus={sourceStatus}
        priority={priority}
        sources={getSourcesUrls(incident)}
        startIncidentResolution={startIncidentResolution}
        incidentResolution={incidentResolution}
      />

      {/* Title and Summary */}
      <div className="mb-4">
        <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-2 line-clamp-2">
          {title}
        </h3>
        {summary !== undefined && (
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-3">
            {summary}
          </p>
        )}
      </div>

      {/* Timeline */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mb-3">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
          <FormattedMessage id="rca.details.timeline" />
        </h4>

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs">
            <Calendar className="w-3 h-3 text-gray-500" />
            <span className="text-gray-600 dark:text-gray-400">
              <FormattedMessage id="incidents.card.started" />:
            </span>
            <span className="text-gray-900 dark:text-gray-100">
              {formatDate(startedAt)}
            </span>
          </div>

          {resolvedAt !== undefined && resolvedAt.length > 0 && (
            <div className="flex items-center gap-2 text-xs">
              <Clock className="w-3 h-3 text-gray-500" />
              <span className="text-gray-600 dark:text-gray-400">
                <FormattedMessage id="incidents.card.resolved" />:
              </span>
              <span className="text-gray-900 dark:text-gray-100">
                {formatDate(resolvedAt)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Slack Thread Link - only show if there's a Slack incident */}
      {incident.slackIncident?.url !== undefined && (
        <a
          href={incident.slackIncident.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button variant="outline" size="sm" className="w-full">
            <MessageSquare className="w-4 h-4 mr-2" />
            <FormattedMessage id="rca.details.viewSlackThread" />
          </Button>
        </a>
      )}
    </div>
  );
};
